import React from 'react';
import { motion } from 'framer-motion';

const EASE = [0.22, 1, 0.36, 1];

/* Asymmetric hairline Bento — sharp edges, oversized metrics, editorial prose.
   No generic icons, no rounded boxes. The 1px gap on a white/10 background
   creates the hairline-grid look; tiles sit on the deep base colour. */
const TILES = [
  {
    n: '01', span: 'md:col-span-8', metric: '60s', metricSuffix: 'to plastic state',
    title: 'Surgical Moldability',
    body: 'After a 60-second hydration in saline or autologous blood, the rigid ceramic becomes a plastic, conformable scaffold — cut with scissors, bent to irregular margins, or milled dry for patient-specific implants. Intra-operative adaptability replaces pre-fabricated sizing, cutting operating-room time and material waste.',
    hero: true,
  },
  {
    n: '02', span: 'md:col-span-4', metric: '50%', metricSuffix: 'faster healing',
    title: 'Accelerated Ossification',
    body: 'High specific surface area drives Ca²⁺ and PO₄³⁻ release at the interface, upregulating osteoblast migration. Healing falls to 28 days against the 56-day standard.',
  },
  {
    n: '03', span: 'md:col-span-4', metric: '100%', metricSuffix: 'synthetic',
    title: 'Zero Contamination Risk',
    body: 'The curdlan polymer is a bacterial exopolysaccharide — no prion, virus or zoonotic transmission. Its triple-helix is neutral to macrophages, with no foreign-body inflammatory response.',
  },
  {
    n: '04', span: 'md:col-span-4', metric: 'PRP·BMP', metricSuffix: 'loadable',
    title: 'Antibiotic Carrier Matrix',
    body: 'The porous HAP-curdlan matrix absorbs aminoglycoside antibiotics (e.g. gentamicin), platelet-rich plasma or growth-factor solutions — a localized release system delivering them directly at the implant site.',
  },
  {
    n: '05', span: 'md:col-span-4', metric: 'Precision', metricSuffix: 'dry-state milling',
    title: 'Anatomical Conformance',
    body: 'In its dry state, the composite is precisely machined or milled using standard dental or orthopedic instrumentation. These micro-milled segments preserve complete structural porosity, ionic reactivity, and surface bioactivity while adapting natively to irregular, patient-specific anatomical boundaries.',
  },
  {
    n: '06', span: 'md:col-span-12', metric: '3-Year', metricSuffix: 'shelf life · no cold chain',
    title: 'Ambient Storage, Animal-Free',
    body: 'A fully synthetic origin removes the ethical and logistical burden of animal-derived substitutes. FlexiOss® stores at ambient temperature — no cold chain, no special handling — simplifying procurement across the Philippines and Southeast Asia.',
    wide: true,
  },
];

const Tile = ({ t, i }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-60px' }}
    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: i * 0.075 }}
    className={`${t.span} ${t.hero ? 'md:row-span-2' : ''}`}
  >
    <div
      data-testid={`bento-tile-${t.n}`}
      className={`group relative flex h-full flex-col justify-between border border-transparent bg-[#000d1a] p-8 bento-card-hover ${t.hero ? 'md:p-12' : ''} ${t.wide ? 'md:flex-row md:items-end md:gap-12' : ''}`}
    >
      <div className={`${t.wide ? 'md:max-w-md' : ''}`}>
        <div className="flex items-center gap-3">
          <span className="font-mono text-[11px] font-bold tabular-nums text-blue-400">{t.n}</span>
          <span className="h-px w-8 bg-blue-500/50 transition-all duration-500 group-hover:w-14 group-hover:bg-blue-400" />
        </div>
        <p className={`mt-6 font-display font-black leading-none tracking-tighter text-white ${t.hero ? 'text-7xl lg:text-8xl' : 'text-5xl'}`}>
          {t.metric}
        </p>
        <p className="mt-2 font-display text-[11px] uppercase tracking-[0.22em] text-white/40">{t.metricSuffix}</p>
      </div>
      <div className={`${t.hero ? 'mt-10' : 'mt-7'} ${t.wide ? 'md:mt-0 md:flex-1' : ''}`}>
        <h3 className={`font-display font-bold leading-snug text-white transition-colors duration-300 group-hover:text-blue-300 ${t.hero ? 'text-2xl lg:text-3xl' : 'text-lg'}`}>
          {t.title}
        </h3>
        <p className={`mt-3 font-light leading-relaxed text-slate-400 ${t.hero ? 'text-base lg:text-lg' : 'text-sm'}`}>
          {t.body}
        </p>
      </div>
    </div>
  </motion.div>
);

const InnovativePropertiesBento = () => {
  return (
    <section id="innovation" data-testid="innovation-section" className="bg-[#000a14] px-6 py-24 sm:px-10 lg:px-16">
      <div className="mx-auto max-w-[1600px]">
        <div className="mb-12 flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <p className="font-display text-[11px] font-bold uppercase tracking-[0.4em] text-blue-400">Mechanism of Action</p>
            <h2 className="mt-5 font-display text-4xl font-black leading-[1.02] tracking-tight text-white sm:text-5xl lg:text-6xl">
              What makes FlexiOss<sup className="text-2xl">®</sup> different.
            </h2>
          </div>
          <p className="max-w-sm font-light leading-relaxed text-slate-400">
            Six clinically validated properties that separate FlexiOss® from xenograft, allograft and first-generation
            synthetic substitutes.
          </p>
        </div>

        {/* Hairline bento grid */}
        <div className="grid grid-cols-1 gap-px bg-white/10 md:grid-cols-12 md:auto-rows-[minmax(0,auto)]">
          {TILES.map((t, i) => (
            <Tile key={t.n} t={t} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default InnovativePropertiesBento;
